import pdfMake from 'pdfmake/build/pdfmake';
import { LOGO_BASE64 } from '../assets/base64Logo';
import { FontService } from './fontService';

export class PDFGenerator {

  constructor(checklist, clientName, equipment, period, clientCanvas, footerSettings, inspectorName, secondTechnicianName) {
    this.checklist = checklist;
    this.clientName = clientName;
    this.equipment = equipment;
    this.period = period;
    this.clientCanvas = clientCanvas;
    this.footerSettings = footerSettings;
    this.inspectorName = inspectorName || 'Inspector';
    this.inspectorSignature = null; // Will be set before generating PDF
    
    // Use the passed second technician name parameter instead of trying to get it from the checklist
    this.secondTechnicianName = secondTechnicianName && secondTechnicianName !== 'No Aplica' ? secondTechnicianName : null;
    
    this.initializeFonts();
  }

  async initializeFonts() {
    const fonts = await FontService.getFontUrls();
    pdfMake.fonts = {
      Roboto: fonts
    };
  }

  // Add method to set inspector signature
  setInspectorSignature(signatureData) {
    this.inspectorSignature = signatureData;
  }

  getBase64Image(canvas) {
    try {
      if (!canvas) return null;
      // Return the full data URL instead of splitting it
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error converting canvas to base64:', error);
      return null;
    }
  }

  formatDateEU(date) {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getFlattenedItems() {
    if (!this.checklist) return [];
    
    if (this.checklist.sections?.length > 0) {
      const flatItems = [];
      
      this.checklist.sections.forEach(section => {
        // Handle direct items under section
        if (section.items?.length) {
          section.items.forEach(item => {
            flatItems.push({
              description: `${section.title} > ${item.description}`,
              status: item.status,
              comments: item.comments
            });
          });
        }
        
        // Handle subsections
        section.subsections?.forEach(subsection => {
          subsection.items?.forEach(item => {
            flatItems.push({
              description: `${section.title} > ${subsection.title} > ${item.description}`,
              status: item.status,
              comments: item.comments
            });
          });
        });
      });
      
      return flatItems;
    }
    
    // If no sections, just return items directly
    return this.checklist.items.map(item => ({
      description: item.description,
      status: item.status,
      comments: item.comments
    }));
  }

  loadLogo() {
    return LOGO_BASE64;  // Just return the base64 string directly
  }

  async createDocumentDefinition() {
    const number = this.checklist.templateNumber?.toString() || '';
    const rev = (this.checklist.rev || '').toString();
    const combinedNumber = this.checklist.combinedNumber?.toString() || '';

    const logoDataUrl = this.loadLogo(); // No need for await anymore

    const headerLogo = {
      stack: [
        logoDataUrl ? { 
          image: logoDataUrl,
          width: 60,
          alignment: 'center'
        } : { text: 'LOGO', alignment: 'center' },
        { text: 'ESITSA', alignment: 'center', fontSize: 8 },
        { text: 'Servicios de Ingenieria', alignment: 'center', fontSize: 8 }
      ]
    };

    const signatureConfig = {
      width: 150,
      height: 60,  // Reduced from 70 to 60 for better proportion
      scale: 0.8   // Add scaling factor to make signatures more consistent
    };

    const inspectorSignature = this.inspectorSignature ? {
      image: this.inspectorSignature,
      width: signatureConfig.width,
      height: signatureConfig.height,
      scale: signatureConfig.scale
    } : { text: '[No signature]' };

    const clientSignature = this.clientCanvas ? {
      image: this.getBase64Image(this.clientCanvas),
      width: signatureConfig.width,
      height: signatureConfig.height,
      scale: signatureConfig.scale
    } : { text: '[No signature]' };

    const items = this.getFlattenedItems();
    const contentTables = [
      // Header table
      {
        table: {
          widths: ['40%', '10%', '*'],
          body: [[
            { text: 'Item', style: 'tableHeader' },
            { text: 'Estado', style: 'tableHeader' },
            { text: 'Observaciones', style: 'tableHeader' }
          ]]
        },
        layout: {
          fillColor: () => '#3399ff',
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#aaa',
          vLineColor: () => '#aaa'
        }
      }
    ];
    
    // Create individual tables for each row
    items.forEach(item => {
      const parts = item.description.split(' > ');
      const descriptionContent = parts.length > 1 
        ? {
            stack: [
              { 
                text: parts.slice(0, -1).join(' > '),
                fontSize: 9,
                color: '#666666',
                italics: true
              },
              {
                text: parts[parts.length - 1],
                fontSize: 10
              }
            ]
          }
        : {
            text: item.description,
            fontSize: 9
          };

      contentTables.push({
        table: {
          widths: ['40%', '10%', '*'],
          body: [[
            { 
              ...descriptionContent,
              margin: [2, 1]
            },
            {
              text: item.status,
              margin: [2, 1],
              alignment: 'center'
            },
            {
              text: item.comments || '',
              margin: [2, 1],
              fontSize: 9,
              noWrap: false
            }
          ]]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#aaa',
          vLineColor: () => '#aaa',
          fillColor: contentTables.length % 2 === 1 ? '#f8f9fa' : null,
          defaultBorder: true
        },
        unbreakable: true
      });
    });

    // Modify the signatures section to use 50-50 split and add second tech below
    const signaturesBody = [[
      {
        stack: [
          inspectorSignature,
          { text: this.inspectorName, margin: [0, 5] },
          { text: 'Técnico', margin: [0, 3], color: '#3b3b3b', fontSize: 8 }
        ],
        alignment: 'center',
        margin: [0, 10, 0, 0]
      },
      {
        stack: [
          clientSignature,
          { text: 'Cliente', margin: [0, 5] }
        ],
        alignment: 'center',
        margin: [0, 10, 0, 0]
      }
    ]];

    // Add safe title handling
    const titleText = (() => {
      const originalTitle = this.checklist.originalTitle || this.checklist.title || '';
      // Only try to split if it's a string and contains ' - '
      if (typeof originalTitle === 'string' && originalTitle.includes(' - ')) {
        return originalTitle.split(' - ')[0];
      }
      return originalTitle;
    })();

    // Content array should include conditionally the general comments
    const contentArray = [
      // Client Information
      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              `Cliente: ${this.clientName}`,
              `Equipo: ${this.equipment}`
            ],
            [
              `Período: ${this.period}`,
              `Fecha: ${this.formatDateEU(new Date())}`
            ]
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#aaa',
          vLineColor: () => '#aaa'
        },
        margin: [0, 0, 0, 20]
      },
      // Items Tables 
      ...contentTables,
    ];

    // Add general comments section only if there are comments
    if (this.checklist.generalComments) {
      contentArray.push({
        stack: [
          { text: 'Observaciones Generales', style: 'sectionHeader', color: '#333333', margin: [0, 20, 0, 10] },
          {
            table: {
              widths: ['*'],
              body: [[
                { text: this.checklist.generalComments, margin: [5, 5], fontSize: 9 }
              ]]
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#aaa',
              vLineColor: () => '#aaa'
            },
            margin: [0, 0, 0, 20]
          }
        ],
        unbreakable: true  // This will keep the entire general comments section together
      });
    }

    // Add signatures
    contentArray.push({
      table: {
        widths: ['50%', '50%'],
        body: signaturesBody
      },
      layout: 'noBorders',
      margin: [0, 20],
      unbreakable: true
    });

    // Add second technician if name exists and not "no_aplica"
    if (this.secondTechnicianName) {
      contentArray.push({
        text: [
          { text: 'Segundo Técnico: ', bold: true, fontSize: 10 },
          { text: this.secondTechnicianName, fontSize: 10 }
        ],
        margin: [0, 10],
        alignment: 'left'
      });
    }

    return {
      pageSize: 'A4',
      pageMargins: [40, 120, 40, 60], // Reduced from 140 to 120
      header: (currentPage, pageCount) => ({
        margin: [40, 10, 40, 0],
        stack: [
          {
            table: {
              widths: ['25%', '*', '25%'],
              body: [[
                headerLogo,
                {
                    stack: [
                        { text: '', margin: [0, 17] },
                        {
                  text: titleText, // Use the safely processed title
                  alignment: 'center',
                  fontSize: 12,
                  bold: true
                }
            ]
                },
                {
                  stack: [
                    { text: 'Check', alignment: 'center', fontSize: 9 },
                    { text: `Nº ${number}`, alignment: 'center', fontSize: 9 },
                    { text: `Rev. ${rev}`, alignment: 'center', fontSize: 9 },
                    { text: '', margin: [0, 5] },
                    combinedNumber ? { text: combinedNumber, alignment: 'center', fontSize: 12, bold: true } : {},
                    { text: '', margin: [0, 5] },
                    { text: `Hoja ${currentPage} de ${pageCount}`, alignment: 'center', fontSize: 9 }
                  ]
                }
              ]]
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#aaa',
              vLineColor: () => '#aaa'
            }
          },
          { text: '', margin: [0, 5] } // Reduced spacer margin from 10 to 5
        ]
      }),
      footer: () => {
        const footerParts = [];
        if (this.footerSettings?.address) footerParts.push(this.footerSettings.address);
        if (this.footerSettings?.phone) footerParts.push(this.footerSettings.phone);

        return {
          margin: [40, 20, 40, 0],
          stack: [
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }] },
            { text: footerParts.join(' • '), alignment: 'center', fontSize: 7, color: 'grey', margin: [0, 5, 0, 0] }
          ]
        };
      },
      content: contentArray,
      styles: {
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: 'white',
          alignment: 'left',
          margin: [5, 5]
        },
        sectionHeader: {
          bold: true,
          fontSize: 11,  // Slightly larger font
          color: 'white',  // White text for contrast
          margin: [5, 7],  // More vertical padding
        },
        subsectionHeader: {
          bold: true,
          fontSize: 9,
          margin: [7, 5],
          color: '#1a202c'  // Dark text for subsections
        }
      },
      defaultStyle: {
        fontSize: 9,
        font: 'Roboto' // Explicitly set the font
      }
    };
  }

  async generate() {
    await this.initializeFonts();
    const docDefinition = await this.createDocumentDefinition();
    return new Promise((resolve) => {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBlob((blob) => {
        resolve(blob);
      });
    });
  }
}
